/**
 * Thin Pixi.js scene runtime.
 *
 * One ``Application`` per mounted ``PixiCanvas``, one active ``Scene``
 * at a time.  The scene receives state updates via a pull-based
 * ``redraw`` hook -- React's Zustand subscription tells us *when*,
 * the scene asks the store *what*.  This keeps Pixi code free of
 * React types and Zustand free of Pixi types.
 *
 * Phase 4 ships one scene: ``BuildingExteriorScene``.  Phase 5+ will
 * register ``PodRoomScene`` / ``FrozenBasementScene`` / ``PartyHallScene``
 * here.  The manager owns the camera transitions between scenes.
 */

import type { Application } from 'pixi.js';
import { Container } from 'pixi.js';

export interface SceneContext {
  app: Application;
  /** Get the latest snapshot of the dashboard store -- read-only here. */
  getState: () => SceneStateSnapshot;
}

/** Frozen subset of the dashboard store that scenes can react to. */
export interface SceneStateSnapshot {
  buildingFloors: SceneFloor[];
  selectedPodId: string | null;
  /**
   * Detail snapshot for the currently selected pod -- populated by the
   * Pixi snapshot builder when ``view.kind === 'pod_room'``, ``null``
   * on the building view.  The ``PodRoomScene`` reads this to render
   * the per-agent + PM state without taking a React or Zustand
   * dependency.
   */
  selectedPod: SceneSelectedPod | null;
  weatherKind: string | null;
  sunPhase: string | null;
  /** -90..+90; > 0 means sun above the horizon, < 0 means moon/night sky. */
  sunElevationDeg: number | null;
  /** 0..360, where 90 = east, 180 = south, 270 = west. */
  sunAzimuthDeg: number | null;
  /** True while the market is open -- pod windows can ramp brightness. */
  isMarketOpen: boolean | null;
  /**
   * Latest ``scene_hint.camera`` directive translated to a pod-focus
   * spotlight.  ``podId`` is the focused pod (or null for whole-
   * building directives like ``building_overview``).  ``setAtMs`` is
   * a ``performance.now()`` timestamp the scene uses to fade the
   * effect after a couple seconds.
   */
  cameraFocus: {
    kind: string;
    podId: string | null;
    setAtMs: number;
  } | null;
  /** Daily close lifecycle.  ``started`` dims the building so the
   *  "office closing" mood reads visually; ``completed`` returns to
   *  normal.  Null between days. */
  dailyClose: { phase: 'started' | 'completed'; at: string } | null;
}

export interface SceneFloor {
  id: string;
  label: string;
  pods: ScenePod[];
}

export interface ScenePod {
  podId: string;
  name: string;
  riskState: 'active' | 'watch' | 'reduced' | 'frozen';
  isCanary: boolean;
  haircutPct: number;
  /** Discipline-gate flag severity (1-4) -- coloured dot on the pod window. */
  disciplineSeverity: 'low' | 'medium' | 'high' | 'critical' | null;
  /** Center-book scale (e.g. 0.85) -- shown as a "scale" badge when != 1.0. */
  podScale: number | null;
  /** True while a cycle is in flight -- paints a soft accent border. */
  cycleInFlight: boolean;
  /** Today's PnL in absolute currency units (from PodSummary). */
  dailyPnl: number | null;
  /** Today's return as a fraction (e.g., 0.0042 = +0.42%). */
  dailyReturn: number | null;
  /** Current net asset value -- starts at ``capital`` and drifts with PnL. */
  nav: number | null;
  /** Set briefly when an agent.signal_emitted event lands -- scenes use
   * this for a window-flicker animation. */
  flickerUntilMs: number;
  /** ``performance.now()`` deadline set when a ``risk.state_changed``
   * event flips the pod to ``frozen``.  Scenes draw an ice-crack
   * overlay until this timestamp passes -- a one-shot animation that
   * draws attention to the freeze event without distracting from the
   * rest of the building. */
  iceUntilMs: number;
  /** ``performance.now()`` timestamp of the most recent event for
   * this pod (any type).  Building view draws a pulsing "alive"
   * dot on pods with activity in the last ~30 s so users can see
   * which seats are warm at a glance. */
  lastActivityAt: number;
  /** Latest ``scene_hint.intensity`` for any event on this pod
   *  (``low | medium | high | critical``).  Building view uses this
   *  to scale the cycle-in-flight border pulse so a critical event
   *  is visible from the exterior, not just inside the room. */
  lastIntensity: string | null;
  /** True while the pod is mid-floor-transition: ``room.exited`` has
   *  landed but the matching ``room.entered`` has not.  Building view
   *  paints a moving badge so the staircase shuffle is visible from
   *  the exterior. */
  isMoving: boolean;
  /** Latest discipline code (e.g. ``suspected_overtrading``) -- powers
   *  the hover tooltip on the severity dot. */
  disciplineCode: string | null;
  /** Latest discipline detail blob -- key/value pairs the tooltip
   *  formats into a "Details" block.  Empty record when no flag. */
  disciplineDetail: Readonly<Record<string, unknown>>;
  /** ISO ts of the latest discipline flag (for relative-age in tooltip). */
  disciplineRaisedAt: string | null;
  /** Latest policy action (applied / shadow_recommended / rolled_back / no_change)
   *  -- powers the policy badge near the pod window + tooltip. */
  policyAction: ScenePolicyAction | null;
  /** PolicyUpdater modifiers applied to this pod (from REST + live policy
   *  events).  Tag chips on the pod window. */
  currentModifiers: Readonly<Record<string, unknown>>;
  /** Risk reason text from latest ``risk.state_changed`` (or REST fallback).
   *  Shown in the frozen-pod tooltip alongside ``riskSince``. */
  riskReason: string | null;
  /** ISO ts when the pod entered its current risk state. */
  riskSince: string | null;
  /** Pod's base capital allocation (RUB) -- used for tooltip context. */
  capital: number;
  /** Newest-last NAV trajectory for the pod-window sparkline.  Bounded
   *  at ~16 points in the store reducer. */
  navHistory: ReadonlyArray<ScenePodNavPoint>;
}

/** One point on the per-pod NAV sparkline. */
export interface ScenePodNavPoint {
  ts: string;
  nav: number;
  /** Fractional intraday return at this point (``null`` until the
   *  session-open NAV is known so we don't draw a flat 0% baseline). */
  intradayReturn: number | null;
}

/** Subset of policy action info the scene needs.  Mirrors
 *  ``PodPolicyAction`` in the store but kept Pixi-free here. */
export interface ScenePolicyAction {
  kind: 'applied' | 'shadow_recommended' | 'rolled_back' | 'no_change';
  triggerRule: string | null;
  paramChanged: string | null;
  valueAfter: unknown;
  decidedAt: string | null;
}

/** Per-agent runtime state inside the selected pod's room. */
export interface SceneAgent {
  actor: string;
  thinking: boolean;
  lastSignal: number | null;
  lastConfidence: number | null;
  topTickers: readonly string[];
  mood: string | null;
  /** Latest ``scene_hint.intensity`` for this agent -- low / medium /
   *  high / critical.  ``null`` means "no hint, default to medium". */
  intensity: string | null;
  /** ISO-8601 ts of the last meaningful update -- used for bubble fade-out. */
  lastUpdatedAt: string | null;
  /** Latest ``agent.idle_action.kind`` (e.g. ``qa.candles``, ``sa.twitter``).
   *  Drives the per-action visual reaction window in the pod-room scene
   *  (eyes look down for reading, head tilt for charts, ...). */
  lastPhraseKind: string | null;
  /** ISO-8601 ts of the latest idle_action.  Reaction lasts ~800 ms past
   *  this point, then the agent falls back to ambient breathing. */
  lastPhraseAt: string | null;
  /** The actual ambient sentence the backend emitted (e.g.
   *  ``"листает свечные графики"``).  Falls back to ``null`` when no
   *  idle_action has landed for this agent.  Used by the scene to
   *  show the phrase in the speech bubble between signal events. */
  lastPhrase: string | null;
}

/**
 * Map scene_hint.intensity ("low" | "medium" | "high" | "critical") to a
 * dimensionless multiplier.  Used by every scene that wants animations
 * to speed up / glow brighter when the backend marks an event as
 * dramatic.  ``null`` and unknown strings fall back to medium (1.0).
 */
export function intensityToMultiplier(intensity: string | null): number {
  switch (intensity) {
    case 'low':
      return 0.7;
    case 'high':
      return 1.4;
    case 'critical':
      return 2.0;
    default:
      return 1.0;
  }
}

/** PM seat state inside the selected pod's room. */
export interface ScenePm {
  thinking: boolean;
  lastUiSummary: string | null;
  confidence: number | null;
  targetGross: number | null;
  intentsCount: number | null;
  decisionAt: string | null;
  /** Top portfolio weights from the latest decision. */
  topWeights: readonly { ticker: string; weight: number }[];
}

/** A single order tick in the per-pod recent-orders ribbon. */
export interface SceneOrder {
  id: string;
  type: 'submitted' | 'filled' | 'rejected';
  ticker: string;
  /** ISO timestamp -- the scene uses ``now - ts`` for fade-out. */
  ts: string;
  detail: string;
}

/** Per-pod detail snapshot for the pod-room scene. */
export interface SceneSelectedPod {
  podId: string;
  name: string;
  floor: string;
  riskState: 'active' | 'watch' | 'reduced' | 'frozen' | null;
  haircutPct: number | null;
  agents: Record<string, SceneAgent | undefined>;
  pm: ScenePm;
  /**
   * Newest-first list of order events tied to this pod -- powers the
   * Phase 7 trading-floor ticker rail.  Bounded at 25 in the reducer.
   */
  recentOrders: readonly SceneOrder[];
}

export interface Scene {
  /** Add Pixi children to ``container``; called once when the scene
   * becomes active. */
  enter(ctx: SceneContext, container: Container): void;
  /** Detach + free any heavy assets; called when the scene is
   * replaced. */
  exit(): void;
  /** Re-read state and update Pixi children.  Called on every store
   * push and on every animation frame the scene cares about. */
  redraw(ctx: SceneContext): void;
  /** Optional per-frame tween / animation tick. */
  tick?(ctx: SceneContext, deltaMs: number): void;
}

export class SceneManager {
  private active: Scene | null = null;
  private container: Container;
  private lastTickMs = performance.now();

  constructor(private readonly ctx: SceneContext) {
    this.container = new Container();
    ctx.app.stage.addChild(this.container);
    ctx.app.ticker.add(this.onTick);
  }

  setScene(scene: Scene): void {
    if (this.active === scene) return;
    this.active?.exit();
    this.container.removeChildren();
    this.active = scene;
    scene.enter(this.ctx, this.container);
    scene.redraw(this.ctx);
  }

  /** Pulled from React on every relevant store change. */
  pushStateUpdate(): void {
    this.active?.redraw(this.ctx);
  }

  destroy(): void {
    this.ctx.app.ticker.remove(this.onTick);
    this.active?.exit();
    this.active = null;
    this.container.destroy({ children: true });
  }

  private onTick = (): void => {
    const now = performance.now();
    const delta = now - this.lastTickMs;
    this.lastTickMs = now;
    this.active?.tick?.(this.ctx, delta);
  };
}
